import { useState, useRef, useEffect } from 'react';
import { useTemplateApi } from './hooks/useTemplateApi';
import { useModificationLogic } from 'components/PhotoAdminComponents/ModifierForm/hooks/useModificationLogic';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import queries from 'api/queries/ProductQueries';
import { useSelector } from 'react-redux';

export const useTemplatesLogic = () => {
  const { t } = useTranslation();
  const {
    templates,
    getTemplates,
    getTemplate,
    templatesLoading,
    postTemplate,
    putTemplate,
    deleteTemplate,
    templatesError,
    getTemplateBody,
    template,
    setTemplate,
    templateLoading,
    createProgress,
    updateProgress,
    deleteProgress
  } = useTemplateApi();
  const { getCurrentModifiers, clearModifiers } = useModificationLogic();
  const shopId = useSelector((state) => state.nonPersistedReducers?.shopSliceReducer?.shopId);
  const [isTemplate, setIsTemplate] = useState(false);
  const [clickedTemplate, setClickedTemplate] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formChanged, setFormChanged] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [currentModifiers, setCurrentModifiers] = useState([]);
  const [name, setName] = useState(t('templates.newTemplate'));
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const buttonRef = useRef(null);

  const { data: modifiers } = useQuery({
    queryKey: ['currentModifiers'],
    queryFn: () => queries.getModifiers(shopId)
  });

  useEffect(() => {
    getTemplates();
  }, []);

  useEffect(() => {
    if (modifiers?.data) {
      setCurrentModifiers(getCurrentModifiers([], modifiers?.data));
    }
  }, [modifiers?.data]);

  useEffect(() => {
    if (template) {
      setName(template?.name);
      if (template?.modifiers?.length > 0) {
        setCurrentModifiers(getCurrentModifiers(template, modifiers?.data));
      }
    }
  }, [template]);

  useEffect(() => {
    if (templatesError) {
      setIsDialogOpen(true);
    }
  }, [templatesError]);

  const confirmSelect = () => {
    if (clickedTemplate && buttonRef.current) {
      setSelectedTemplate(clickedTemplate);
      getTemplate(clickedTemplate);
    }
  };

  const resetForm = () => {
    setName(t('templates.newTemplate'));
    setClickedTemplate(null);
    setSelectedTemplate(null);
    setCurrentModifiers(getCurrentModifiers([], modifiers?.data));
    setTemplate(null);
  };

  const handleTemplateOperations = {
    delete: async () => {
      if (buttonRef.current) {
        await deleteTemplate(selectedTemplate);
        await getTemplates();
        resetForm();
      }
    },
    update: async () => {
      if (buttonRef.current) {
        await buttonRef.current.click();
        await putTemplate(getTemplateBody(selectedTemplate, name, clearModifiers(currentModifiers)));
        await getTemplates();
        resetForm();
      }
    },
    create: async () => {
      if (buttonRef.current) {
        await buttonRef.current.click();
        await postTemplate(getTemplateBody(null, name, clearModifiers(currentModifiers)));
        await getTemplates();
        resetForm();
      }
    }
  };

  return {
    templates,
    templatesLoading,
    templatesError,
    template,
    templateLoading,
    isTemplate,
    setIsTemplate,
    clickedTemplate,
    setClickedTemplate,
    selectedTemplate,
    formChanged,
    setFormChanged,
    disabled,
    setDisabled,
    currentModifiers,
    modifiers,
    setCurrentModifiers,
    name,
    setName,
    buttonRef,
    openDeleteDialog,
    setOpenDeleteDialog,
    openUpdateDialog,
    setOpenUpdateDialog,
    openCreateDialog,
    setOpenCreateDialog,
    isDialogOpen,
    setIsDialogOpen,
    confirmSelect,
    handleTemplateOperations,
    createProgress,
    updateProgress,
    deleteProgress
  };
};
